import { GroupBase, OptionsOrGroups } from "react-select";
import {
  CPServiceObject,
  NextRevisionMode,
  NextRevisionPeriodUnit,
} from "../api/models/unified";
import serviceObjectsClient from "../api/private/serviceObjectsClient";
import { ServiceObjectTableQuery } from "../pages/serviceObjects/models";
import { SelectItem } from "../components/select/Select";
import { handleApiErrorWithNotification } from "./errorHandling";
import { NetworkError } from "../common/models";
import { useCustomTranslation } from "./translateHelpers";
import { useCallback } from "react";

export const getServiceObjectSelectDescription = (
  serviceObject: CPServiceObject,
) => {
  let subtitle = "";

  if (serviceObject.identificationNumber && !serviceObject.location) {
    subtitle = serviceObject.identificationNumber;
  }

  if (!serviceObject.identificationNumber && serviceObject.location) {
    subtitle = serviceObject.location;
  }

  if (serviceObject.identificationNumber && serviceObject.location) {
    subtitle = `${serviceObject.identificationNumber} | ${serviceObject.location}`;
  }

  return (
    <div>
      <div>{serviceObject.oneLinerAddress}</div>
      <div>{subtitle}</div>
    </div>
  );
};

export const loadServiceObjectsForSelect = async (
  queryParams: ServiceObjectTableQuery,
  callback: (
    options: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
  ) => void,
) => {
  try {
    const result = await serviceObjectsClient.getServiceObjects(queryParams);

    callback(
      result.data.map((serviceObject) => ({
        value: serviceObject.id,
        label: serviceObject.name,
        description: getServiceObjectSelectDescription(serviceObject),
      })),
    );
  } catch (e) {
    handleApiErrorWithNotification(e as NetworkError);
  }
};

export const useNextRevisionHelpers = () => {
  const { t, tServiceObject } = useCustomTranslation();

  const translateRevisionMode = useCallback(
    (mode: NextRevisionMode) => {
      return tServiceObject(`next_revision_mode_${mode}`);
    },
    [tServiceObject],
  );

  const translateRevisionPeriod = useCallback(
    (periodValue: number, periodUnit: NextRevisionPeriodUnit) => {
      if (periodUnit === "Day") {
        return t("days", { count: periodValue });
      }

      return t("months", { count: periodValue });
    },
    [t],
  );

  const translateRevisionModeWithLabel = useCallback(
    (mode: NextRevisionMode) =>
      tServiceObject("revision_mode_with_value", {
        mode: translateRevisionMode(mode),
      }),
    [tServiceObject, translateRevisionMode],
  );

  const translateRevisionPeriodWithLabel = useCallback(
    (periodValue: number, periodUnit: NextRevisionPeriodUnit) =>
      tServiceObject("revision_period_with_value", {
        value: translateRevisionPeriod(periodValue, periodUnit),
      }),
    [tServiceObject, translateRevisionPeriod],
  );

  return {
    translateRevisionMode,
    translateRevisionPeriod,
    translateRevisionModeWithLabel,
    translateRevisionPeriodWithLabel,
  };
};
